/* WIENtage shop behaviour.
   The bag talks to Shopify's own cart, so stock and checkout stay authoritative.
   Every listing is one physical vintage scarf, so a line can never exceed one. */

const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

const R = window.WIENTAGE.routes;
const S = window.WIENTAGE.strings;

/* ------------------------------------------------------------------ money -- */

function money(cents) {
  const value = (cents / 100).toLocaleString('de-AT', {
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  });
  return `€ ${value}`;
}

/* ------------------------------------------------------------------- cart -- */

const Cart = {
  async get() {
    const res = await fetch(`${R.cart}.js`, { headers: { Accept: 'application/json' } });
    return res.json();
  },

  /* One physical scarf per listing, so refuse a second of the same variant. */
  async add(variantId) {
    const cart = await Cart.get();
    if (cart.items.some((i) => String(i.variant_id) === String(variantId))) {
      return { ok: false, reason: 'duplicate' };
    }

    const res = await fetch(`${R.cart_add}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      return { ok: false, reason: body.description || 'error' };
    }
    return { ok: true };
  },

  async remove(key) {
    await fetch(`${R.cart_change}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
  },

  async paint() {
    const cart = await Cart.get();

    $$('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.dataset.empty = cart.item_count === 0;
    });

    const body = $('[data-cart-body]');
    if (!body) return;

    if (!cart.items.length) {
      body.innerHTML =
        `<p class="cart-note" style="padding-top:8px">${S.empty}</p>` +
        `<a class="btn btn--wide" href="/collections/all">${S.browse}</a>`;
    } else {
      body.innerHTML = cart.items
        .map(
          (item) => `
          <div class="cart-line">
            <img src="${item.image ? item.image.replace(/(\.[^.]*)$/, '_160x$1') : ''}" alt="${item.product_title}" loading="lazy">
            <div>
              <h4>${item.product_title}</h4>
              <div class="meta">${item.sku || ''}</div>
              <div class="meta">${item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : ''}</div>
            </div>
            <div style="text-align:right">
              <div>${money(item.final_line_price)}</div>
              <button class="cart-line__remove" type="button" data-remove="${item.key}">${S.remove}</button>
            </div>
          </div>`
        )
        .join('');
    }

    /* Mit einem Rabatt zeigt die erste Zeile die Summe vor dem Abzug, darunter
       stehen der Abzug mit seinem Namen und die Endsumme. Ohne Rabatt bleibt es
       bei der einen Zeile, sonst stuende dieselbe Zahl dreimal untereinander. */
    const abzug = cart.total_discount > 0;

    const total = $('[data-cart-total]');
    if (total) total.textContent = money(abzug ? cart.original_total_price : cart.total_price);

    const offRow = $('[data-cart-off]');
    if (offRow) {
      offRow.hidden = !abzug;
      const erste = (cart.cart_level_discount_applications || [])[0];
      const label = $('[data-cart-off-label]');
      const amount = $('[data-cart-off-amount]');
      if (abzug && label && erste && erste.title) label.textContent = erste.title;
      if (abzug && amount) amount.textContent = '\u2212' + money(cart.total_discount);
    }

    const grand = $('[data-cart-grand]');
    if (grand) {
      grand.hidden = !abzug;
      const grandAmount = $('[data-cart-grand-amount]');
      if (abzug && grandAmount) grandAmount.textContent = money(cart.total_price);
    }

    const foot = $('[data-cart-foot]');
    if (foot) foot.hidden = cart.items.length === 0;
  }
};

/* --------------------------------------------------------------- drawers -- */

let lastFocus = null;

function openDrawer(id) {
  const drawer = $(`#${id}`);
  if (!drawer) return;
  lastFocus = document.activeElement;
  drawer.classList.add('is-open');
  drawer.setAttribute('aria-hidden', 'false');
  $('#drawer-backdrop').classList.add('is-open');
  document.body.classList.add('no-scroll');
  const focusable = $('button, a, input', drawer);
  if (focusable) focusable.focus();
}

function closeDrawers() {
  /* The lightbox and the search overlay are not `.drawer`s visually (centered
     or top-anchored and fading rather than sliding in from an edge), but they
     open and close the same way, so they ride the same is-open/aria-hidden
     toggle instead of a second mechanism. */
  $$('.drawer, .lightbox, .search-overlay').forEach((d) => {
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
  });
  $('#drawer-backdrop').classList.remove('is-open');
  document.body.classList.remove('no-scroll');
  if (lastFocus) lastFocus.focus();
}

let toastTimer;
function toast(message) {
  const el = $('#toast');
  /* Erst sichtbar machen, dann den Text setzen. Die Meldung ist eine
     Live-Region, und Vorleseprogramme lesen nichts vor, was im Moment der
     Textaenderung verborgen ist. Solange sie immer sichtbar war, fiel das
     nicht auf. */
  el.classList.add('is-visible');
  el.textContent = message;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('is-visible'), 3200);
}

/* ------------------------------------------------------- collection tools -- */

/* Every query below is scoped to one [data-collection-scope], so two listing
   sections on the same page filter their own grid instead of fighting over
   the first one they find. */
function initFilterScope(scope) {
  const grid = $('[data-collection-grid]', scope);
  if (!grid) return;

  const cards = $$('[data-card]', grid);

  /* Mehrfachauswahl: pro Gruppe eine Menge gewaehlter Werte, leer heisst alle.
     Die Gruppen werden aus den Kaestchen gelesen, nicht festgeschrieben: eine
     neue Filtergruppe ins Markup zu stellen genuegt, hier ist nichts zu tun. */
  const boxes = $$('[data-filter]', scope);
  const groups = [...new Set(boxes.map((b) => b.dataset.filter))];
  const state = {};
  groups.forEach((group) => { state[group] = new Set(); });

  /* Zahlenbereiche: Umrechnung und Beschriftung stehen im Markup, damit ein
     dritter Bereich spaeter eine Zeile Liquid ist und keine Zeile hier. */
  const ranges = $$('[data-range]', scope).map((el) => ({
    el,
    key: el.dataset.range,
    scale: Number(el.dataset.scale || 1),
    min: $('[data-range-min]', el),
    max: $('[data-range-max]', el)
  }));

  const inGroups = (card, skip) =>
    groups.every(
      (group) => group === skip || state[group].size === 0 || state[group].has(card.dataset[group])
    );

  const inRanges = (card, skip) =>
    ranges.every(({ key, scale, min, max }) => {
      if (key === skip) return true;
      const lo = min && min.value ? Number(min.value) * scale : -Infinity;
      const hi = max && max.value ? Number(max.value) * scale : Infinity;
      const value = Number(card.dataset[key] || 0);
      return value >= lo && value <= hi;
    });

  /* "ab 80 €", "bis 150 cm", "80 bis 150 €". Die Woerter stehen im Markup,
     damit hier keine deutschen Zeichenketten liegen. */
  const rangeText = ({ el, min, max }) => {
    const from = min && min.value;
    const to = max && max.value;
    const unit = el.dataset.unit || '';
    if (from && to) return `${from} ${el.dataset.wordTo} ${to} ${unit}`.trim();
    if (from) return `${el.dataset.wordFrom} ${from} ${unit}`.trim();
    if (to) return `${el.dataset.wordTo} ${to} ${unit}`.trim();
    return '';
  };

  const optName = (input) => $('.facet__name', input.closest('.facet__opt')).textContent;

  /* Trefferzahlen neben jedem Wert, gerechnet ohne die eigene Gruppe: sonst
     stuende neben jedem nicht gewaehlten Wert eine Null, sobald in derselben
     Gruppe etwas angekreuzt ist. Werte ohne Treffer werden abgeblendet und
     abgeschaltet, es sei denn, sie sind selbst angekreuzt. */
  const paintCounts = () => {
    boxes.forEach((box) => {
      const group = box.dataset.filter;
      const hits = cards.filter(
        (card) => card.dataset[group] === box.value && inGroups(card, group) && inRanges(card)
      ).length;
      const opt = box.closest('.facet__opt');
      const out = $('[data-facet-count]', opt);
      if (out) out.textContent = hits;
      opt.classList.toggle('is-empty', hits === 0 && !box.checked);
      box.disabled = hits === 0 && !box.checked;
    });
  };

  /* Was in einer Gruppe gewaehlt ist, steht an der Schaltflaeche selbst: ein
     Wert im Klartext, mehrere als Zahl. Ohne das muesste man jedes Feld
     aufklappen, um zu sehen, was gesetzt ist. */
  const paintPicks = () => {
    $$('.facet', scope).forEach((facet) => {
      const out = $('[data-facet-pick]', facet);
      if (!out) return;
      const key = facet.dataset.facet;
      let text = '';

      if (state[key]) {
        const chosen = [...state[key]];
        if (chosen.length === 1) {
          const box = $(`[data-filter="${key}"][value="${chosen[0]}"]`, facet);
          text = box ? optName(box) : chosen[0];
        } else if (chosen.length > 1) {
          text = String(chosen.length);
        }
      }

      const range = ranges.find((r) => r.key === key);
      if (range) text = rangeText(range);

      out.textContent = text;
      out.hidden = text === '';
      facet.classList.toggle('is-set', text !== '');
    });
  };

  const chip = (text, onRemove) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'fchip';
    b.append(text);
    const x = document.createElement('span');
    x.className = 'fchip__x';
    x.setAttribute('aria-hidden', 'true');
    x.textContent = '✕';
    b.append(x);
    b.setAttribute('aria-label', `${text}: ${S.removeFilter}`);
    b.addEventListener('click', onRemove);
    return b;
  };

  /* Die gewaehlten Filter als Chips unter der Zeile, jeder einzeln
     wegklickbar. Ohne diese Reihe muss man jedes Feld aufklappen, um zu sehen,
     was gesetzt ist. */
  const paintChips = () => {
    const box = $('[data-active-chips]', scope);
    const list = $('[data-chip-list]', scope);
    if (!box || !list) return;
    list.textContent = '';

    groups.forEach((group) => {
      state[group].forEach((value) => {
        const input = $(`[data-filter="${group}"][value="${value}"]`, scope);
        const name = input ? optName(input) : value;
        list.append(
          chip(name, () => {
            if (input) input.checked = false;
            state[group].delete(value);
            refresh();
          })
        );
      });
    });

    ranges.forEach((range) => {
      const text = rangeText(range);
      if (!text) return;
      list.append(
        chip(`${range.el.dataset.label} ${text}`, () => {
          if (range.min) range.min.value = '';
          if (range.max) range.max.value = '';
          refresh();
        })
      );
    });

    box.hidden = list.children.length === 0;
  };

  const apply = () => {
    let shown = 0;
    cards.forEach((card) => {
      const visible = inGroups(card) && inRanges(card);
      card.hidden = !visible;
      if (visible && card.dataset.sold !== 'true') shown += 1;
    });

    const count = $('[data-result-count]', scope);
    if (count) count.textContent = count.dataset.template.replace('%%COUNT%%', shown);

    const show = $('[data-show-count]', scope);
    if (show) show.textContent = show.dataset.template.replace('%%COUNT%%', shown);

    const empty = $('[data-empty-state]', scope);
    if (empty) empty.hidden = cards.some((c) => !c.hidden);

    /* Die Anzahl gesetzter Filter am "Filter"-Knopf des Telefons: dort ist die
       Zeile zugeklappt, ohne Zahl sieht man nicht, dass gefiltert wird. */
    const active =
      groups.reduce((n, group) => n + state[group].size, 0) +
      ranges.filter((r) => rangeText(r)).length;
    const badge = $('[data-active-count]', scope);
    if (badge) {
      badge.textContent = active;
      badge.hidden = active === 0;
    }
  };

  const refresh = () => {
    apply();
    paintCounts();
    paintPicks();
    paintChips();
  };

  boxes.forEach((box) =>
    box.addEventListener('change', () => {
      const group = box.dataset.filter;
      if (box.checked) state[group].add(box.value);
      else state[group].delete(box.value);
      refresh();
    })
  );

  ranges.forEach(({ min, max }) => {
    if (min) min.addEventListener('input', refresh);
    if (max) max.addEventListener('input', refresh);
  });

  $$('[data-reset-filters]', scope).forEach((reset) =>
    reset.addEventListener('click', () => {
      boxes.forEach((box) => { box.checked = false; box.disabled = false; });
      groups.forEach((group) => state[group].clear());
      ranges.forEach(({ min, max }) => {
        if (min) min.value = '';
        if (max) max.value = '';
      });
      refresh();
    })
  );

  /* ------- die Felder auf- und zuklappen ------- */

  const closeFacets = (except) =>
    $$('.facet', scope).forEach((facet) => {
      if (facet === except) return;
      $('[data-facet-toggle]', facet).setAttribute('aria-expanded', 'false');
      $('[data-facet-panel]', facet).hidden = true;
    });

  $$('[data-facet-toggle]', scope).forEach((btn) =>
    btn.addEventListener('click', () => {
      const facet = btn.closest('.facet');
      const panel = $('[data-facet-panel]', facet);
      const open = panel.hidden;
      closeFacets(facet);
      panel.hidden = !open;
      btn.setAttribute('aria-expanded', String(open));
    })
  );

  /* Ein Klick daneben schliesst das offene Feld. Innerhalb der Leiste nicht:
     auf dem Handy liegen alle Felder in einem Blatt, ein Klick darin ist kein
     Klick daneben. Escape schliesst es ebenfalls, sonst kommt man mit der
     Tastatur nur wieder heraus, indem man dieselbe Schaltflaeche findet. */
  document.addEventListener('click', (e) => {
    if (!scope.contains(e.target)) closeFacets(null);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = $$('[data-facet-toggle]', scope).find((b) => b.getAttribute('aria-expanded') === 'true');
    if (!open) return;
    closeFacets(null);
    open.focus();
  });

  /* ------- das Blatt auf dem Handy ------- */

  const sheet = $('[data-filter-sheet]', scope);
  const opener = $('[data-filter-open]', scope);
  const backdrop = $('#drawer-backdrop');

  const sheetOpen = () => sheet && sheet.classList.contains('is-open');

  const openSheet = () => {
    sheet.classList.add('is-open');
    if (opener) opener.setAttribute('aria-expanded', 'true');
    if (backdrop) backdrop.classList.add('is-open');
    document.body.classList.add('no-scroll');
  };

  const closeSheet = () => {
    sheet.classList.remove('is-open');
    if (opener) opener.setAttribute('aria-expanded', 'false');
    if (backdrop) backdrop.classList.remove('is-open');
    document.body.classList.remove('no-scroll');
    if (opener) opener.focus();
  };

  if (opener && sheet) {
    opener.addEventListener('click', openSheet);
    $$('[data-filter-close]', scope).forEach((b) => b.addEventListener('click', closeSheet));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && sheetOpen()) closeSheet();
    });
    if (backdrop) backdrop.addEventListener('click', () => { if (sheetOpen()) closeSheet(); });
  }

  /* ------- sortieren ------- */

  /* Sortiert wird an den Karten, nicht ueber die Adresszeile: so gilt es auf
     der Startseite genauso wie auf einer Sammlungsseite, es wirft die aktiven
     Filter nicht weg, und der Zustand ist ueberhaupt nur so sortierbar, der
     steht in einem Tag. Verkauftes bleibt in jeder Reihenfolge hinten, das ist
     der Sinn der Liste. */
  const grades = { A: 0, B: 1, C: 2 };
  const sortValue = (card, key) => {
    if (key === 'price') return Number(card.dataset.price || 0);
    if (key === 'date') return Number(card.dataset.created || 0);
    if (key === 'grade') return grades[card.dataset.grade] ?? 9;
    return card.dataset.title || '';
  };

  const sortCards = (mode) => {
    const [key, dir] = mode.split('-');
    /* "grade" und "title" kommen ohne Richtung, beide sollen aufsteigen:
       Zustand A vor C, A vor Z. Nur ein ausdrueckliches "desc" dreht um. */
    const sign = dir === 'desc' ? -1 : 1;
    const order = [...cards].sort((a, b) => {
      const soldA = a.dataset.sold === 'true';
      const soldB = b.dataset.sold === 'true';
      if (soldA !== soldB) return soldA ? 1 : -1;
      const va = sortValue(a, key);
      const vb = sortValue(b, key);
      if (typeof va === 'string') return va.localeCompare(vb, 'de');
      return (va - vb) * sign;
    });
    order.forEach((card) => grid.appendChild(card));
  };

  /* Auf dem Handy steht die Sortierung ueber dem Bestand, auf dem Rechner in
     der Zeile. Beide zeigen denselben Zustand. */
  const sorters = $$('[data-sort]', scope);
  sorters.forEach((sel) =>
    sel.addEventListener('change', () => {
      sorters.forEach((other) => { other.value = sel.value; });
      sortCards(sel.value);
    })
  );
  if (sorters.length) sortCards(sorters[0].value);

  refresh();
}

/* Der Anfrage-Knopf im Feed fuehrt auf die Produktseite und soll das Formular
   dort gleich offen finden: der Weg vom Wischen zur Frage darf nicht durch ein
   zweites Suchen des Knopfes fuehren. Dasselbe gilt fuer einen geteilten Link
   mit #anfrage. */
function openHashDrawer() {
  if (location.hash !== '#anfrage') return;
  const drawer = $('#inquiry-drawer');
  if (drawer) openDrawer('inquiry-drawer');
}

/* --------------------------------------------------------- Feed (Handy) -- */

/* Ein Schal pro Bildschirm, senkrecht wischen fuer den naechsten, waagrecht
   fuer die Fotos dieses einen. Gescrollt wird die Seite selbst, hier wird
   also nichts nachgebaut: das JS zaehlt nur mit, merkt Stuecke und schaltet
   zwischen hell und dunkel. */
function initMobileFeed() {
  const feed = $('[data-mfeed]');
  if (!feed) return;

  /* Der erste Schal soll ohne Wischen ganz zu sehen sein. Was ueber dem Feed
     steht, ist je nach Einstellungen unterschiedlich hoch (Laufband,
     Zusagenzeile, Kopf), also wird es gemessen statt geraten. */
  const measure = () => {
    const chrome = Math.max(0, Math.round(feed.getBoundingClientRect().top + window.scrollY));
    feed.style.setProperty('--mfeed-chrome', chrome + 'px');
  };
  measure();
  window.addEventListener('resize', measure);

  /* ---- hell oder dunkel, die Wahl des Besuchers ---- */

  /* Der Schalter sitzt in jedem Abschnitt, damit nichts oben am Bildschirm
     kleben muss. Alle zeigen denselben Zustand. */
  const modes = $$('[data-mfeed-mode]', feed);

  const paint = (dark) => {
    feed.classList.toggle('mfeed--dark', dark);
    modes.forEach((b) => {
      b.setAttribute('aria-pressed', String(dark));
      /* Die verborgene Beschriftung nennt, was der Knopf einschaltet, nicht
         was gerade gilt. */
      const label = $('[data-mfeed-mode-label]', b);
      if (label) label.textContent = dark ? S.feedLight : S.feedDark;
      b.setAttribute('aria-label', dark ? S.feedLight : S.feedDark);
    });
  };

  let dark = false;
  try { dark = localStorage.getItem('wt-feed-dark') === '1'; } catch (e) {}
  paint(dark);

  modes.forEach((b) =>
    b.addEventListener('click', () => {
      dark = !dark;
      paint(dark);
      try { localStorage.setItem('wt-feed-dark', dark ? '1' : '0'); } catch (e) {}
    })
  );

  const slides = $$('[data-mslide]', feed);

  /* ---- die Punkte unter der Fotostrecke ---- */

  $$('[data-mshots]', feed).forEach((strip) => {
    const dots = $('[data-mdots]', strip.parentElement);
    if (!dots) return;
    strip.addEventListener(
      'scroll',
      () => {
        const i = Math.round(strip.scrollLeft / strip.clientWidth);
        [...dots.children].forEach((d, n) => d.classList.toggle('on', n === i));
      },
      { passive: true }
    );
  });

  /* ---- merken, im Browser des Besuchers ---- */

  let saved = [];
  try { saved = JSON.parse(localStorage.getItem('wt-saved') || '[]'); } catch (e) {}

  const isSaved = (id) => saved.indexOf(String(id)) !== -1;

  const paintSaved = (id) => {
    $$('[data-msave="' + id + '"]').forEach((b) => b.setAttribute('aria-pressed', String(isSaved(id))));
  };

  const toggleSave = (id) => {
    id = String(id);
    saved = isSaved(id) ? saved.filter((x) => x !== id) : saved.concat(id);
    try { localStorage.setItem('wt-saved', JSON.stringify(saved)); } catch (e) {}
    paintSaved(id);
  };

  $$('[data-msave]', feed).forEach((b) => {
    paintSaved(b.dataset.msave);
    b.addEventListener('click', () => toggleSave(b.dataset.msave));
  });

  /* Doppeltippen merkt, wie man es kennt. Ein einzelner Tipp darf weiterhin
     der Titel oder ein Knopf sein, deshalb wird nur auf der Flaeche gezaehlt. */
  slides.forEach((slide) => {
    const save = $('[data-msave]', slide);
    if (!save) return;
    let last = 0;
    slide.addEventListener('click', (e) => {
      if (e.target.closest('a, button')) return;
      const now = Date.now();
      if (now - last < 320) toggleSave(save.dataset.msave);
      last = now;
    });
  });

  /* ---- das Blatt zur Note ---- */

  const sheet = $('[data-msheet]', feed);
  if (sheet) {
    const title = $('[data-msheet-title]', sheet);
    const text = $('[data-msheet-text]', sheet);
    const close = () => { sheet.classList.remove('is-open'); sheet.setAttribute('aria-hidden', 'true'); };

    $$('[data-mgrade]', feed).forEach((b) =>
      b.addEventListener('click', () => {
        title.textContent = b.dataset.title;
        text.textContent = b.dataset.text;
        sheet.classList.add('is-open');
        sheet.setAttribute('aria-hidden', 'false');
      })
    );

    $('[data-msheet-close]', sheet).addEventListener('click', close);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && sheet.classList.contains('is-open')) close();
    });
  }

  /* Das Kreuz fuehrt auf die Startseite. Kam der Besucher von woanders, ist
     der Schritt zurueck der bessere: wer aus dem Raster in den Feed gegangen
     ist, will zurueck ins Raster. */
  const close = $('[data-mfeed-close]', feed);
  if (close) {
    close.addEventListener('click', (e) => {
      if (window.history.length > 1) {
        e.preventDefault();
        window.history.back();
      }
    });
  }

  /* "Von vorn": auf der Feed-Seite scrollt der Feed selbst, sonst die Seite. */
  const top = $('[data-mfeed-top]', feed);
  if (top) {
    top.addEventListener('click', () => {
      if (feed.scrollHeight > feed.clientHeight + 4) {
        feed.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        window.scrollTo({ top: feed.getBoundingClientRect().top + window.scrollY, behavior: 'smooth' });
      }
    });
  }
}

function initCollectionFilters(root = document) {
  $$('[data-collection-scope]', root).forEach(initFilterScope);
}

/* ------------------------------------------------------------- lightbox -- */

const Lightbox = {
  images: [],
  index: 0,

  collect() {
    this.images = $$('[data-open="lightbox"]').map((btn) => ({
      src: btn.dataset.full,
      alt: btn.dataset.alt || ''
    }));
  },

  show(i) {
    if (!this.images.length) return;
    this.index = (i + this.images.length) % this.images.length;
    const img = $('#lightbox-img');
    if (!img) return;
    img.src = this.images[this.index].src;
    img.alt = this.images[this.index].alt;
    img.classList.remove('is-zoomed');
    img.style.transformOrigin = '';
    const count = $('#lightbox-count');
    if (count) count.textContent = `${this.index + 1} / ${this.images.length}`;
  },

  /* Zooming toward the point that was clicked, rather than always toward the
     centre, is what makes it useful for reading a label in a corner. */
  pointAt(img, e) {
    const rect = img.getBoundingClientRect();
    img.style.transformOrigin =
      `${((e.clientX - rect.left) / rect.width) * 100}% ${((e.clientY - rect.top) / rect.height) * 100}%`;
  }
};

function initLightbox() {
  Lightbox.collect();
  const img = $('#lightbox-img');
  if (!img) return;

  img.addEventListener('click', (e) => {
    const zoomed = img.classList.toggle('is-zoomed');
    if (zoomed) Lightbox.pointAt(img, e);
  });

  img.addEventListener('mousemove', (e) => {
    if (img.classList.contains('is-zoomed')) Lightbox.pointAt(img, e);
  });
}

/* ------------------------------------------------------------ reveal, misc - */

function observeReveals(root = document) {
  const items = $$('.reveal:not(.is-in)', root);
  if (!('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('is-in'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry, i) => {
        if (!entry.isIntersecting) return;
        setTimeout(() => entry.target.classList.add('is-in'), Math.min(i * 60, 300));
        io.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px' }
  );
  items.forEach((el) => io.observe(el));
}

/* ------------------------------------------------------------ phone bits -- */

/* The product gallery is a swipeable strip on a phone and a grid above that,
   so the counter is only meaningful while it actually scrolls sideways. */
function initGalleryCounter() {
  const gallery = $('[data-gallery]');
  const index = $('[data-gallery-index]');
  const dots = $('[data-gallery-dots]');
  if (!gallery || (!index && !dots)) return;

  gallery.addEventListener(
    'scroll',
    () => {
      if (gallery.scrollWidth <= gallery.clientWidth + 1) return; // grid, not a strip
      const at = Math.round(gallery.scrollLeft / gallery.clientWidth);
      const n = Math.min(at + 1, gallery.children.length);
      if (index) index.textContent = n;
      /* Dieselbe Zahl treibt die Punkte. Ein zweiter Zuhoerer fuer dasselbe
         Ereignis waere eine zweite Stelle, an der dieselbe Rechnung steht. */
      if (dots) [...dots.children].forEach((d, i) => d.classList.toggle('on', i === n - 1));
    },
    { passive: true }
  );
}

/* Die Fotoleiste neben dem grossen Bild. Ein Klick darin tauscht das Bild,
   statt die Lupe zu oeffnen; die Lupe haengt am grossen Bild und bekommt den
   Index des gewaehlten Fotos mit, damit sie an derselben Stelle aufgeht. Auf
   dem Telefon ist die Leiste ausgeblendet, dort wischt man durch den Streifen. */
function initGalleryRail() {
  const rail = $('[data-rail]');
  const gallery = $('[data-gallery]');
  if (!rail || !gallery) return;

  const stage = gallery.querySelector('figure');
  const stageImg = stage && stage.querySelector('img');
  const stageBtn = stage && stage.querySelector('[data-open="lightbox"]');
  if (!stageImg || !stageBtn) return;

  rail.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-thumb]');
    if (!thumb) return;

    /* srcset schlaegt src. Bleibt es stehen, waehlt der Browser weiter aus der
       alten Liste und das Bild wechselt nicht. */
    stageImg.removeAttribute('srcset');
    stageImg.removeAttribute('sizes');
    stageImg.src = thumb.dataset.stage;
    stageImg.alt = thumb.dataset.alt || '';

    stageBtn.dataset.index = thumb.dataset.index;
    stageBtn.dataset.full = thumb.dataset.full;
    stageBtn.dataset.alt = thumb.dataset.alt || '';

    $$('[data-thumb]', rail).forEach((t) => t.classList.toggle('is-active', t === thumb));
  });
}

/* The buy bar comes back along the bottom edge once the real buy row has
   scrolled past, and only then — a bar that is always there just eats screen. */
function initBuyBar() {
  const bar = $('#buy-bar');
  const anchor = $('[data-buy-anchor]');
  if (!bar || !anchor) return;

  if (!('IntersectionObserver' in window)) return;

  const io = new IntersectionObserver(
    ([entry]) => {
      const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', passed);
      bar.setAttribute('aria-hidden', String(!passed));
    },
    { threshold: 0 }
  );
  io.observe(anchor);
}

/* Snapping has to sit on the document: a full-screen feed inside its own
   scroll container fights the phone browser's address bar and nested
   scrolling. The class goes on <html> only when a reel is actually present. */
function initReel() {
  document.documentElement.classList.toggle('reel-mode', Boolean($('[data-reel]')));
}

/* -------------------------------------------------------------- cookie bar - */

const COOKIE_ACK_KEY = 'wientage-cookie-ack';

function initCookieBar() {
  const bar = $('#cookie-bar');
  if (!bar) return;
  let acknowledged = false;
  try {
    acknowledged = localStorage.getItem(COOKIE_ACK_KEY) === '1';
  } catch (e) {
    /* Private browsing or blocked storage: show the notice every visit
       rather than fail silently and never show it at all. */
  }
  if (!acknowledged) bar.classList.add('is-visible');
}

/* Die Aktionszeile im Kopf und die Preise darunter.

   Shopify kennt einen Rabattcode erst im Warenkorb: in Liquid steht am Schal
   immer der Listenpreis, egal was im Code steckt. Wer ueber einen Rabattlink
   kommt, traegt den Code aber als Keks mit sich, und den kann das Skript lesen.
   Steht dort genau der Code aus den Einstellungen, deckt es die Zeile auf und
   rechnet jeden ausgezeichneten Preis herunter: der alte durchgestrichen, der
   neue daneben. Steht er nicht da, passiert gar nichts, damit niemand einen
   Rabatt sieht, den er nicht hat.

   Gerechnet wird aus den Cent am Element, nicht aus dem angezeigten Text:
   sonst haengt der Preis an der Schreibweise der Waehrung. Die Prozentzahl
   muss mit dem Rabatt in Shopify uebereinstimmen, sonst zeigt der Laden etwas
   anderes an, als die Kasse abzieht. Der Hinweis steht in den Einstellungen. */
function euroText(cent) {
  const wert = (cent / 100).toLocaleString('de-AT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return `\u20AC${wert}`;
}

function hatRabattKeks(code) {
  try {
    return document.cookie.split('; ').some((keks) => {
      const teil = keks.indexOf('=');
      if (teil < 0) return false;
      const name = keks.slice(0, teil);
      const wert = decodeURIComponent(keks.slice(teil + 1) || '');
      return name === 'discount_code' && wert.trim().toLowerCase() === code;
    });
  } catch (e) {
    /* Keine Kekse lesbar: dann eben kein Rabatt. */
    return false;
  }
}

function initAktionBar(root = document) {
  const bar = $('[data-aktion]');
  if (!bar) return;

  const gesucht = (bar.dataset.code || '').trim().toLowerCase();
  if (!gesucht) return;

  /* Das Enddatum steht auch am Element, weil eine ausgelieferte Seite im
     Zwischenspeicher liegen bleiben kann: ohne diese Pruefung wuerde sie noch
     Tage spaeter einen Rabatt ausweisen, den die Kasse nicht mehr abzieht. */
  const bis = parseInt(bar.dataset.bis || '0', 10);
  if (bis > 0 && Date.now() / 1000 > bis) {
    bar.hidden = true;
    return;
  }

  const da = hatRabattKeks(gesucht);
  bar.hidden = !da;
  if (!da) return;

  const prozent = parseFloat(bar.dataset.prozent || '0');
  if (!(prozent > 0) || prozent >= 100) return;

  $$('[data-preis]:not([data-preis-fertig])', root).forEach((el) => {
    const cent = parseInt(el.dataset.preis, 10);
    if (!cent) return;
    const neu = Math.round((cent * (100 - prozent)) / 100);
    if (neu >= cent) return;
    el.dataset.preisFertig = '1';
    el.innerHTML =
      `<s class="preis-alt">${euroText(cent)}</s> <span class="preis-neu">${euroText(neu)}</span>`;
  });
}

function initHeader() {
  const header = $('.header');
  if (header)
    window.addEventListener(
      'scroll',
      () => header.classList.toggle('is-stuck', window.scrollY > 8),
      { passive: true }
    );
}

/* ------------------------------------------------------------- delegation -- */

document.addEventListener('click', async (e) => {
  const open = e.target.closest('[data-open]');
  if (open) {
    if (open.dataset.full) Lightbox.show(Number(open.dataset.index || 0));

    /* A trigger can preselect what the enquiry is about — the collection
       button and the photo button open the same form, and asking someone to
       pick the topic they just clicked is a step for nothing. */
    if (open.dataset.topic) {
      const topic = $(`#${open.dataset.open} select[name="contact[topic]"]`);
      if (topic) topic.value = open.dataset.topic;
    }
    return openDrawer(open.dataset.open);
  }

  if (e.target.closest('[data-close]')) return closeDrawers();

  /* Clicking the empty backdrop area closes these two — checked by identity,
     not `closest`, or a click landing inside the form/image would bubble up
     and close the very thing it just opened. */
  if (e.target.matches('.lightbox, .search-overlay')) return closeDrawers();

  const prev = e.target.closest('[data-lightbox-prev]');
  if (prev) return Lightbox.show(Lightbox.index - 1);

  const next = e.target.closest('[data-lightbox-next]');
  if (next) return Lightbox.show(Lightbox.index + 1);

  const add = e.target.closest('[data-add]');
  if (add) {
    e.preventDefault();
    add.disabled = true;
    const result = await Cart.add(add.dataset.add);
    add.disabled = false;

    if (result.ok) {
      toast(S.added);
      await Cart.paint();
      openDrawer('cart-drawer');
    } else {
      toast(result.reason === 'duplicate' ? S.onlyOne : S.error);
      if (result.reason === 'duplicate') openDrawer('cart-drawer');
    }
    return;
  }

  const rm = e.target.closest('[data-remove]');
  if (rm) {
    rm.disabled = true;
    await Cart.remove(rm.dataset.remove);
    await Cart.paint();
  }

  const cookieAck = e.target.closest('[data-cookie-ack]');
  if (cookieAck) {
    $('#cookie-bar')?.classList.remove('is-visible');
    try {
      localStorage.setItem(COOKIE_ACK_KEY, '1');
    } catch (err) {
      /* Nothing to persist to if storage is blocked; the bar still closes
         for this page view. */
    }
  }
});

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeDrawers();
  if (!$('#lightbox')?.classList.contains('is-open')) return;
  if (e.key === 'ArrowLeft') Lightbox.show(Lightbox.index - 1);
  if (e.key === 'ArrowRight') Lightbox.show(Lightbox.index + 1);
});

document.addEventListener('DOMContentLoaded', () => {
  initHeader();
  initAktionBar();
  initCollectionFilters();
  initMobileFeed();
  openHashDrawer();
  initLightbox();
  initGalleryCounter();
  initGalleryRail();
  initBuyBar();
  initReel();
  initCookieBar();
  observeReveals();
  Cart.paint();
});

/* The theme editor re-renders sections without a page load. */
document.addEventListener('shopify:section:load', (e) => {
  /* Scoped to the section that just re-rendered, so the other scopes on the
     page keep the listeners they already have instead of gaining a second set. */
  initCollectionFilters(e.target);
  initAktionBar(e.target);
  initLightbox();
  observeReveals(e.target);
  Cart.paint();
});
